import ModalCloseButton from '../../../../Component/UI/ModalCloseButton';
import { useEffect, useState } from "react";
import styled from "styled-components";


interface OrderBatchMemoModalProps {
  visible: boolean;
  round: number | null;
  existingName: string;
  existingMemo: string | null;
  onSave: (round: number, name: string, memo: string) => void;
  onClose: () => void;
  busy?: boolean;
  error?: string;
  completion?: boolean;
}

/**
 * N차 발주 메모 모달 — 3단계(작성/조회/수정).
 * 목업: Docs/디자인/팝업_N차 발주 메모{, 2, 수정}.png
 * - 메모 없음(연필 아이콘) → 바로 작성 모드(텍스트영역 + "작성완료")
 * - 메모 있음(말풍선 아이콘) → 조회 모드(읽기전용 + "수정하기") → 누르면 수정 모드("수정완료")
 */
const OrderBatchMemoModal = ({
  visible,
  round,
  existingName,
  existingMemo,
  onSave,
  onClose,
  busy = false,
  error = "",
  completion = false,
}: OrderBatchMemoModalProps) => {
  const [editing, setEditing] = useState(!existingMemo);
  const [text, setText] = useState(existingMemo ?? "");
  const [name, setName] = useState(existingName);

  useEffect(() => {
    if (visible) {
      setEditing(!existingMemo);
      setText(existingMemo ?? "");
      setName(existingName);
    }
  }, [visible, existingMemo, existingName]);

  if (!visible || round === null) return null;

  const isWriteMode = !existingMemo; // 최초 작성(연필) — 조회모드 자체가 없음
  const title = completion ? '발주 처리 완료' : editing && !isWriteMode ? "수정하기" : "발주 정보";

  return (
    <Container onClick={onClose}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <Title>
          {title}
          <ModalCloseButton tone="admin" aria-label="발주 메모 닫기" onClick={onClose} />
        </Title>

        {editing ? (
          <>
            <FieldLabel htmlFor={`procurement-name-${round}`}>발주명</FieldLabel>
            <NameInput id={`procurement-name-${round}`} value={name} maxLength={100} disabled={busy}
              onChange={event => setName(event.target.value)} placeholder={`${round}차 발주`} />
            <FieldLabel htmlFor={`procurement-memo-${round}`}>메모</FieldLabel>
            <Textarea
              id={`procurement-memo-${round}`}
              placeholder="메모를 입력해주세요."
              value={text}
              onChange={(e) => setText(e.target.value)}
              autoFocus
              disabled={busy}
            />
            {error && <p role="alert">{typeof error === "string" ? error : "메모를 저장하지 못했습니다."}</p>}
            <SaveBtn type="button" onClick={() => { if (!busy && name.trim()) onSave(round, name.trim(), text); }} disabled={busy || !name.trim()}>
              {busy ? "저장 중..." : completion ? '저장' : isWriteMode ? "작성 완료" : "수정완료"}
            </SaveBtn>
          </>
        ) : (
          <>
            <FieldLabel>발주명</FieldLabel><MemoText className="name">{existingName}</MemoText>
            <FieldLabel>메모</FieldLabel><MemoText>{existingMemo || '메모 없음'}</MemoText>
            <EditBtn type="button" onClick={() => setEditing(true)}>수정하기</EditBtn>
          </>
        )}
      </ModalBox>
    </Container>
  );
};

export default OrderBatchMemoModal;

const Container = styled.div`
  z-index: 200;
  background-color: #00000085;
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const ModalBox = styled.div`
  box-sizing: border-box;
  width: calc(100% - 24px);
  max-width: 360px;
  background-color: var(--admin-theme-surface, white);
  border-radius: 8px;
  padding: 24px;
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  overscroll-behavior: contain;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 16px;
  font-family: var(--font-family-base); font-weight: var(--font-weight-semibold);
  color: var(--admin-ink);
  padding-bottom: 16px;
  border-bottom: 1px solid var(--admin-theme-border, #f0f0f0);
  img {
    cursor: pointer;
  }
`;

const Textarea = styled.textarea`
  margin-top: 20px;
  width: 100%;
  height: 220px;
  box-sizing: border-box;
  border: none;
  outline: none;
  resize: none;
  font-size: 14px;
  font-family: var(--font-family-base); font-weight: var(--font-weight-regular);
  color: var(--admin-ink);
  &::placeholder {
    color: var(--admin-theme-muted, #c6c6c6);
  }
`;

const FieldLabel = styled.label`
  display: block; margin-top: 18px; margin-bottom: 8px; color: var(--admin-theme-text, #444); font-size: 13px;
`;
const NameInput = styled.input`
  width: 100%; min-height: 44px; box-sizing: border-box; padding: 10px 12px;
  border: 1px solid var(--admin-theme-border, #d8d8d8); border-radius: 8px; font-size: 15px;
`;

const MemoText = styled.div`
  min-height: 120px;
  font-size: 14px;
  font-family: var(--font-family-base); font-weight: var(--font-weight-regular);
  color: var(--admin-ink);
  white-space: pre-wrap;
  &.name { min-height: auto; font-family: var(--font-family-base); font-weight: var(--font-weight-semibold); }
`;

const SaveBtn = styled.button`
  width: 100%; border: 0;
  margin-top: 16px;
  padding: 14px;
  border-radius: 8px;
  text-align: center;
  background: linear-gradient(135deg, var(--admin-accent), var(--admin-accent-deep));
  color: white;
  font-size: 15px;
  font-family: var(--font-family-base); font-weight: var(--font-weight-medium);
  cursor: pointer;
  &:disabled { opacity: .55; cursor: default; }
`;

const EditBtn = styled.button`
  width: 100%; background: var(--admin-theme-surface, #fff);
  margin-top: 16px;
  padding: 14px;
  border-radius: 8px;
  text-align: center;
  border: 1px solid var(--admin-accent);
  color: var(--admin-theme-link, var(--admin-accent));
  font-size: 15px;
  font-family: var(--font-family-base); font-weight: var(--font-weight-medium);
  cursor: pointer;
`;
